import { roundToCents } from "@common/helpers/monetary";
import {
    ScheduleLinkedApplication,
    ScheduleLinkedSourceConflictErrorBody,
} from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";

/** A transaction paying (part of) an invoice or bill; `lineItemIds` empty means the lines are unknown. */
export interface ScheduleDocumentLink {
    transactionId: number;
    documentId: string;
    lineItemIds: string[];
    amount: number;
}

export interface PlannableDocumentLine {
    lineItemId: string;
    documentId: string;
    openAmount: number;
}

export type ScheduleDocumentType =
    | ScheduleSourceType.invoice
    | ScheduleSourceType.bill;

export type ScheduleLinkedConflictSides = Pick<
    ScheduleLinkedSourceConflictErrorBody,
    "requested" | "existing"
>;

export function getLinkedConflictDocumentType(
    conflict: ScheduleLinkedConflictSides,
): ScheduleDocumentType {
    const isBill =
        conflict.requested?.sourceType === ScheduleSourceType.bill ||
        conflict.existing.some(
            (application) => application.sourceType === ScheduleSourceType.bill,
        );
    return isBill ? ScheduleSourceType.bill : ScheduleSourceType.invoice;
}

export interface ScheduleDocumentLinkConflict {
    link: ScheduleDocumentLink;
    transactionApplication: ScheduleLinkedApplication;
    documentApplications: ScheduleLinkedApplication[];
}

/**
 * Which document lines each transaction would pay, the way `createAccrualMatches`
 * allocates: lines are paid in order with `min(available, remaining)` until the
 * transaction is spent, where `available` is the explicit allocation when one
 * is given and the open amount otherwise. Transactions are planned in order
 * and each sees what the previous consumed.
 */
export function planDocumentLinks(options: {
    transactions: Array<{ transactionId: number; amount: number }>;
    lines: PlannableDocumentLine[];
    allocations?: Array<{ lineItemId: string; amount: number }>;
}): ScheduleDocumentLink[] {
    const openByLine = new Map(
        options.lines.map((line) => [
            line.lineItemId,
            Math.abs(line.openAmount),
        ]),
    );
    const requested = options.allocations
        ? new Map(
              options.allocations.map((allocation) => [
                  allocation.lineItemId,
                  Math.abs(allocation.amount),
              ]),
          )
        : null;
    const links: ScheduleDocumentLink[] = [];

    for (const transaction of options.transactions) {
        let remaining = Math.abs(transaction.amount);
        for (const line of options.lines) {
            if (remaining <= 0) {
                break;
            }
            const open = openByLine.get(line.lineItemId) ?? 0;
            const available = requested
                ? (requested.get(line.lineItemId) ?? 0)
                : open;
            const amount = Math.min(available, remaining);
            if (amount <= 0) {
                continue;
            }
            remaining = roundToCents(remaining - amount);
            if (requested) {
                requested.set(
                    line.lineItemId,
                    roundToCents(available - amount),
                );
            }
            openByLine.set(line.lineItemId, roundToCents(open - amount));
            links.push({
                transactionId: transaction.transactionId,
                documentId: line.documentId,
                lineItemIds: [line.lineItemId],
                amount,
            });
        }
    }
    return mergeDocumentLinks(links);
}

export function mergeDocumentLinks(
    links: ScheduleDocumentLink[],
): ScheduleDocumentLink[] {
    const merged = new Map<string, ScheduleDocumentLink>();
    for (const link of links) {
        if (link.amount <= 0) {
            continue;
        }
        const key = `${link.transactionId}:${link.documentId}`;
        const existing = merged.get(key);
        if (!existing) {
            merged.set(key, { ...link, lineItemIds: [...link.lineItemIds] });
            continue;
        }
        existing.amount = roundToCents(existing.amount + link.amount);
        for (const lineItemId of link.lineItemIds) {
            if (!existing.lineItemIds.includes(lineItemId)) {
                existing.lineItemIds.push(lineItemId);
            }
        }
    }
    return [...merged.values()];
}

export function documentIdOf(
    application: ScheduleLinkedApplication,
    documentType: ScheduleDocumentType,
): string | null {
    return documentType === ScheduleSourceType.invoice
        ? application.invoiceId
        : application.billId;
}

export function isLinkCoveredByApplication(options: {
    application: Pick<ScheduleLinkedApplication, "sourceLineIds">;
    applicationDocumentId: string | null;
    link: Pick<ScheduleDocumentLink, "documentId" | "lineItemIds">;
}): boolean {
    const { application, link } = options;
    if (options.applicationDocumentId !== link.documentId) {
        return false;
    }
    if (application.sourceLineIds === null || link.lineItemIds.length === 0) {
        return true;
    }
    return link.lineItemIds.some((lineItemId) =>
        application.sourceLineIds?.includes(lineItemId),
    );
}

/** One conflict per (transaction, document) link whose both ends carry a live schedule. */
export function findDocumentLinkConflicts(options: {
    links: ScheduleDocumentLink[];
    applications: ScheduleLinkedApplication[];
    documentType: ScheduleDocumentType;
}): ScheduleDocumentLinkConflict[] {
    const transactionApplications = new Map<
        number,
        ScheduleLinkedApplication
    >();
    for (const application of options.applications) {
        if (
            application.sourceType === ScheduleSourceType.transaction &&
            application.transactionId !== null &&
            !transactionApplications.has(application.transactionId)
        ) {
            transactionApplications.set(application.transactionId, application);
        }
    }
    const documentApplications = options.applications.filter(
        (application) => application.sourceType === options.documentType,
    );

    return mergeDocumentLinks(options.links).flatMap((link) => {
        const transactionApplication = transactionApplications.get(
            link.transactionId,
        );
        if (!transactionApplication) {
            return [];
        }
        const covering = documentApplications.filter((application) =>
            isLinkCoveredByApplication({
                application,
                applicationDocumentId: documentIdOf(
                    application,
                    options.documentType,
                ),
                link,
            }),
        );
        return covering.length === 0
            ? []
            : [
                  {
                      link,
                      transactionApplication,
                      documentApplications: covering,
                  },
              ];
    });
}
